import React, { useState } from 'react';
import { X, Download, ExternalLink } from 'lucide-react';

/**
 * Click any <img> to open it full-screen in a modal.
 * Wraps the child element and adds a click-to-zoom handler.
 *
 * Usage:
 *   <ImageZoom src={url}>
 *     <img src={url} className="..." />
 *   </ImageZoom>
 *
 * Or as a controlled lightbox:
 *   <ImageZoom src={url} open={open} onOpenChange={setOpen} />
 */
export const ImageZoom = ({ src, alt = '', children, className = '', testId = 'image' }) => {
  const [open, setOpen] = useState(false);

  if (!src) return children || null;

  return (
    <>
      <span onClick={() => setOpen(true)} className={`cursor-zoom-in inline-block ${className}`} data-testid={`${testId}-trigger`}>
        {children || <img src={src} alt={alt} className="rounded-md" />}
      </span>
      {open && (
        <div
          className="fixed inset-0 z-[200] bg-black/85 grid place-items-center p-4"
          onClick={() => setOpen(false)}
          data-testid={`${testId}-modal`}
        >
          <div className="relative max-w-[95vw] max-h-[95vh]" onClick={(e) => e.stopPropagation()}>
            <img src={src} alt={alt} className="max-w-[95vw] max-h-[88vh] rounded-md shadow-2xl object-contain bg-white" />
            <div className="absolute top-2 right-2 flex gap-2">
              <a
                href={src}
                target="_blank"
                rel="noreferrer"
                className="bg-white text-black rounded-full h-9 w-9 grid place-items-center hover:bg-[#1D4ED8] hover:text-white"
                title="Open in new tab"
                data-testid={`${testId}-open`}
              >
                <ExternalLink className="h-4 w-4" />
              </a>
              <a
                href={src}
                download
                className="bg-white text-black rounded-full h-9 w-9 grid place-items-center hover:bg-[#1D4ED8] hover:text-white"
                title="Download"
                data-testid={`${testId}-download`}
              >
                <Download className="h-4 w-4" />
              </a>
              <button
                onClick={() => setOpen(false)}
                className="bg-white text-black rounded-full h-9 w-9 grid place-items-center hover:bg-red-500 hover:text-white"
                title="Close"
                data-testid={`${testId}-close`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageZoom;
